import { usePuck } from "@measured/puck";
import { MAX_CONTENT_COLUMNS } from "@/lib/cms-puck/blocks/column-count";
import {
  getColumnTrackSizeOptions,
  type ColumnWidthWeight,
} from "@/lib/cms-puck/layout";
import { Label } from "@/components/ui/label";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

function clampGridColumns(n: unknown): number {
  const v = Number(n);
  if (!Number.isFinite(v)) return 3;
  return Math.max(1, Math.min(MAX_CONTENT_COLUMNS, Math.floor(v)));
}

function normalizeColumnWidths(
  value: ColumnWidthWeight[] | undefined,
  count: number,
): ColumnWidthWeight[] {
  return Array.from({ length: count }, (_, i) => value?.[i] ?? "1");
}

/** Largeurs par piste pour le bloc Grille (1–12 colonnes). */
export function GridColumnWidthsField({
  value,
  onChange,
}: {
  value?: ColumnWidthWeight[];
  onChange: (value: ColumnWidthWeight[]) => void;
}) {
  const cmsNew = useCmsNew();
  const { selectedItem } = usePuck();
  const columnCount = clampGridColumns(selectedItem?.props?.columns);
  const widths = normalizeColumnWidths(value, columnCount);

  return (
    <div className="space-y-3">
      <p className="text-[11px] leading-snug text-slate-500">
        {cmsNew(
          "cms.new.fields.gridColumnWidths.hint",
          "« Contenu (auto) » adapte la colonne à la largeur de son contenu ; les parts se partagent l'espace restant.",
        )}
      </p>
      {widths.map((width, index) => (
        <div key={index} className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-600">
            {cmsNew("cms.new.fields.gridColumnWidths.columnWidth", "Largeur colonne")}{" "}
            {index + 1}
          </Label>
          <Select
            value={width}
            onValueChange={(next) => {
              const updated = [...widths];
              updated[index] = next as ColumnWidthWeight;
              onChange(updated);
            }}
          >
            <SelectTrigger className="h-9 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {getColumnTrackSizeOptions().map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}
